/**
 * Central error map — 17 signal rows per specs/integration-plan.md SS5.2.
 *
 * Every user-facing error surface in the dashboard resolves copy through
 * `errorMessage(signal)`. Unknown or missing signals fall back to the
 * canonical "server-error" entry.
 *
 * The ErrorSignal type is a closed union — adding a new signal requires
 * extending the type AND adding a row to ERROR_MAP.
 */

export type ErrorSignal =
  | "injection-detector"
  | "circuit-breaker"
  | "dispatch-retry-exhausted"
  | "model-fallback-activated"
  | "dead-letter"
  | "validation"
  | "forbidden"
  | "not-found"
  | "not-a-member"
  | "unknown-resource-shape"
  | "user-not-found"
  | "rate-limited"
  | "quota-exceeded"
  | "payment-required"
  | "conflict"
  | "server-error"
  | "network-error";

export interface ErrorCopy {
  title: string;
  body: string;
  recoveryAction?: { label: string; href?: string; onClick?: () => void };
  severity: "info" | "warning" | "error";
}

export const ERROR_MAP: Record<ErrorSignal, ErrorCopy> = {
  "injection-detector": {
    title: "Suspicious input shielded",
    body: "We detected unexpected content in the input and stopped the request to keep your workspace safe. Try rephrasing the request.",
    severity: "warning",
  },
  "circuit-breaker": {
    title: "Temporarily unavailable",
    body: "This service is taking a short break. Try again in a minute.",
    severity: "warning",
  },
  "dispatch-retry-exhausted": {
    title: "We tried three times and stopped",
    body: "The system attempted this action three times without success. Open the run trace for the underlying error and decide whether to retry.",
    severity: "error",
  },
  "model-fallback-activated": {
    title: "Switched to a backup model",
    body: "The primary model was unavailable; the request completed using a fallback. Quality may differ.",
    severity: "info",
  },
  "dead-letter": {
    title: "Could not deliver after retries",
    body: "We were unable to deliver this message after the maximum number of retries. The full payload is preserved for diagnostics.",
    severity: "error",
  },
  "validation": {
    title: "Some inputs need attention",
    body: "One or more inputs did not match the expected shape. Review the highlighted fields and try again.",
    severity: "warning",
  },
  "forbidden": {
    title: "You don't have access",
    body: "Your current role doesn't permit this action. Ask a workspace administrator if you believe this is a mistake.",
    severity: "error",
  },
  "not-found": {
    title: "We couldn't find that",
    body: "The thing you're trying to reach doesn't exist or has been removed.",
    severity: "error",
  },
  "not-a-member": {
    title: "You're not a member of this workspace",
    body: "Ask a workspace administrator to invite you, or switch to a workspace you belong to.",
    severity: "error",
  },
  "unknown-resource-shape": {
    title: "Unexpected response",
    body: "The server returned data we didn't recognise. The team has been notified. Try again in a moment.",
    severity: "error",
  },
  "user-not-found": {
    title: "Your account couldn't be loaded",
    body: "We couldn't find your account record. Try signing in again.",
    severity: "error",
  },
  "rate-limited": {
    title: "Slow down a little",
    body: "You're sending requests faster than we can process them. Pause for a few seconds and try again.",
    severity: "warning",
  },
  "quota-exceeded": {
    title: "Quota reached",
    body: "You've hit the limit for your current plan. Review your plan usage.",
    severity: "warning",
    recoveryAction: { label: "View plan usage", href: "/admin/plan" },
  },
  "payment-required": {
    title: "Payment needed",
    body: "Your plan requires a billing update to continue.",
    severity: "error",
    recoveryAction: { label: "Open billing", href: "/admin/plan" },
  },
  "conflict": {
    title: "Someone changed this first",
    body: "This item was updated by another person while you were working. Refresh to see the latest version.",
    severity: "warning",
  },
  "server-error": {
    title: "Something went wrong on our end",
    body: "We've logged the error. Try again in a moment.",
    severity: "error",
  },
  "network-error": {
    title: "Connection problem",
    body: "We couldn't reach the server. Check your network connection and try again.",
    severity: "error",
  },
};

/**
 * Resolve an error signal string to user-facing copy.
 *
 * Falls back to "server-error" when the signal is undefined, null,
 * empty, or not a recognised ErrorSignal.
 */
export function errorMessage(signal: string | undefined | null): ErrorCopy {
  if (signal && signal in ERROR_MAP) return ERROR_MAP[signal as ErrorSignal];
  return ERROR_MAP["server-error"];
}
